import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useT } from "../i18n";
import { useFmt, fmtDateTime } from "../i18n/fmt";
import type { Lang } from "../i18n/langStore";

// ── Types ─────────────────────────────────────────────────────────────────────

type ActivityEntry = {
  id: string;
  created_at: string;
  user_email: string | null;
  user_role: string | null;
  organization_name: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  detail: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
};

type ListResp = { total: number; page: number; page_size: number; items: ActivityEntry[] };

type Stats = {
  events_today: number;
  logins_today: number;
  active_users_week: number;
  most_active_org: string | null;
  action_distribution: { action: string; count: number }[];
  daily_activity: { day: string; count: number }[];
};

type OrgOption = { id: string; name: string };

// ── Action metadata (icono, label, color por categoría) ───────────────────────

// labelKey → clave i18n (`activity.<labelKey>`); resuelta con t() al render.
const ACTION_META: Record<string, { labelKey: string; color: string; bg: string }> = {
  // Auth
  "auth.login":                      { labelKey: "actLogin",              color: "#0891b2", bg: "#e0f2fe" },
  "auth.password_reset_requested":   { labelKey: "actPasswordReset",      color: "#0891b2", bg: "#e0f2fe" },
  // Admin — usuarios
  "admin.impersonate":               { labelKey: "actImpersonate",        color: "#7c3aed", bg: "#ede9fe" },
  "admin.user_created":              { labelKey: "actUserCreated",        color: "#059669", bg: "#d1fae5" },
  "admin.user_updated":              { labelKey: "actUserUpdated",        color: "#d97706", bg: "#fef3c7" },
  "admin.user_deactivated":          { labelKey: "actUserDeactivated",    color: "#dc2626", bg: "#fee2e2" },
  "admin.user_deleted":              { labelKey: "actUserDeleted",        color: "#dc2626", bg: "#fee2e2" },
  // Admin — orgs
  "admin.org_created":               { labelKey: "actOrgCreated",         color: "#059669", bg: "#d1fae5" },
  "admin.org_updated":               { labelKey: "actOrgUpdated",         color: "#d97706", bg: "#fef3c7" },
  "admin.logo_uploaded":             { labelKey: "actLogoUploaded",       color: "#0891b2", bg: "#e0f2fe" },
  // Portfolio
  "portfolio.product_added":         { labelKey: "actProductAdded",       color: "#059669", bg: "#d1fae5" },
  "portfolio.product_removed":       { labelKey: "actProductRemoved",     color: "#dc2626", bg: "#fee2e2" },
  "portfolio.competitor_added":      { labelKey: "actCompetitorAdded",    color: "#059669", bg: "#d1fae5" },
  "portfolio.competitor_removed":    { labelKey: "actCompetitorRemoved",  color: "#dc2626", bg: "#fee2e2" },
  // Alertas
  "alerts.subscription_created":     { labelKey: "actAlertCreated",       color: "#059669", bg: "#d1fae5" },
  "alerts.subscription_deleted":     { labelKey: "actAlertDeleted",       color: "#dc2626", bg: "#fee2e2" },
};

// Color por rol (cápsula tintada suave; etiqueta funcional).
const ROLE_COLOR: Record<string, string> = {
  superadmin: "#dc2626",
  admin:      "#f59e0b",
  analyst:    "#0891b2",
  readonly:   "#64748b",
};
function roleColor(role: string): string {
  return ROLE_COLOR[role.toLowerCase()] ?? "#64748b";
}

function actionMeta(action: string): { labelKey: string | null; color: string; bg: string } {
  if (ACTION_META[action]) return ACTION_META[action];
  // Fallback por prefijo (sin labelKey → se muestra la acción cruda)
  if (action.startsWith("auth."))  return { labelKey: null, color: "#0891b2", bg: "#e0f2fe" };
  if (action.startsWith("admin.")) return { labelKey: null, color: "#7c3aed", bg: "#ede9fe" };
  return { labelKey: null, color: "#6b7280", bg: "#f3f4f6" };
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  const fmt = useFmt();
  return (
    <div style={{
      background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
      borderRadius: 18, padding: "1rem 1.25rem", flex: 1, minWidth: 160,
      backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
      boxShadow: "var(--clr-shadow-sm)",
    }}>
      <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: "1.7rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1 }}>
        {typeof value === "number" ? fmt.num(value) : value}
      </div>
      {sub && <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ActionBadge({ action }: { action: string }) {
  const t = useT();
  const m = actionMeta(action);
  const label = m.labelKey ? t(`activity.${m.labelKey}`) : action;
  // Acción = tipo → cápsula tintada suave SIN punto (el punto es de estados).
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: m.color + "1e", color: m.color,
      padding: "3px 11px", borderRadius: 999,
      fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function DetailCell({ detail }: { detail: Record<string, unknown> | null }) {
  if (!detail || Object.keys(detail).length === 0) return <span style={{ color: "var(--clr-text4)" }}>—</span>;
  const entries = Object.entries(detail);
  return (
    <span style={{ fontSize: "0.7rem", color: "var(--clr-text3)" }}>
      {entries.slice(0, 3).map(([k, v]) => (
        <span key={k} style={{ marginRight: 8 }}>
          <span style={{ color: "var(--clr-text4)" }}>{k}:</span>{" "}
          <span>{String(v)}</span>
        </span>
      ))}
      {entries.length > 3 && <span style={{ color: "var(--clr-text4)" }}>+{entries.length - 3}</span>}
    </span>
  );
}

function formatDate(iso: string, lang: Lang) {
  return fmtDateTime(iso, lang, {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

// ── Main component ────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const inputStyle: React.CSSProperties = {
  background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)",
  borderRadius: 12, padding: "6px 12px", fontSize: "0.8rem",
  color: "var(--clr-text)", outline: "none",
};

// labelKey → clave i18n (`activity.<labelKey>`); resuelta con t() al render.
const ACTION_GROUPS = [
  { value: "", labelKey: "grpAll" },
  { value: "auth", labelKey: "grpAuth" },
  { value: "admin.user", labelKey: "grpUsers" },
  { value: "admin.org", labelKey: "grpOrgs" },
  { value: "admin.impersonate", labelKey: "grpImpersonate" },
  { value: "portfolio.product", labelKey: "grpPortfolio" },
  { value: "portfolio.competitor", labelKey: "grpCompetitors" },
  { value: "alerts", labelKey: "grpAlerts" },
];

export default function ActivityPage() {
  const t = useT();
  const fmt = useFmt();
  // Datos
  const [stats, setStats] = useState<Stats | null>(null);
  const [items, setItems] = useState<ActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [actions, setActions] = useState<string[]>([]);

  // Filtros
  const [filterOrg, setFilterOrg] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [page, setPage] = useState(1);

  // UI state
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Cargar orgs y acciones (una vez)
  useEffect(() => {
    api.get<{ id: string; name: string }[]>("/organizations")
      .then(data => setOrgs(data.map(o => ({ id: o.id, name: o.name }))))
      .catch(() => {});
    api.get<string[]>("/activity/actions")
      .then(setActions)
      .catch(() => {});
    api.get<Stats>("/activity/stats")
      .then(setStats)
      .catch(() => {});
  }, []);

  // Cargar lista con filtros
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    if (filterOrg)    params.set("org_id", filterOrg);
    if (filterAction) params.set("action", filterAction);
    if (filterFrom)   params.set("date_from", new Date(filterFrom).toISOString());
    if (filterTo)     params.set("date_to", new Date(filterTo + "T23:59:59").toISOString());
    api.get<ListResp>(`/activity?${params}`)
      .then(r => { setItems(r.items); setTotal(r.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, filterOrg, filterAction, filterFrom, filterTo]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="page" style={{ color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("activity.title")}</h2>
        <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
          {t("activity.subtitle")}
        </p>
      </div>

      {/* ── Stats ─────────────────────────────────────────────────────────── */}
      {stats && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          <StatCard label={t("activity.statEventsToday")}   value={stats.events_today} />
          <StatCard label={t("activity.statLoginsToday")}   value={stats.logins_today} />
          <StatCard label={t("activity.statActiveUsers")}   value={stats.active_users_week} />
          <StatCard
            label={t("activity.statMostActiveOrg")}
            value={stats.most_active_org ?? "—"}
          />
        </div>
      )}

      {/* ── Filtros ───────────────────────────────────────────────────────── */}
      <div style={{
        background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
        borderRadius: 18, padding: "0.85rem 1.25rem", marginBottom: 16,
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
        backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
        boxShadow: "var(--clr-shadow-sm)",
      }}>
        {/* Org */}
        <select value={filterOrg} onChange={e => { setFilterOrg(e.target.value); setPage(1); }} style={inputStyle}>
          <option value="">{t("activity.allOrgs")}</option>
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>

        {/* Acción */}
        <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} style={inputStyle}>
          {ACTION_GROUPS.map(g => <option key={g.value} value={g.value}>{t(`activity.${g.labelKey}`)}</option>)}
          {actions.filter(a => !ACTION_GROUPS.some(g => g.value === a)).map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>

        {/* Fechas */}
        <input type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(1); }}
          style={inputStyle} placeholder={t("activity.fromPlaceholder")} />
        <input type="date" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(1); }}
          style={inputStyle} placeholder={t("activity.toPlaceholder")} />

        {/* Reset */}
        {(filterOrg || filterAction || filterFrom || filterTo) && (
          <button
            onClick={() => { setFilterOrg(""); setFilterAction(""); setFilterFrom(""); setFilterTo(""); setPage(1); }}
            style={{ ...inputStyle, borderRadius: 999, cursor: "pointer", color: "var(--clr-text3)", display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            {t("activity.clear")}
          </button>
        )}

        <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "var(--clr-text4)" }}>
          {t("activity.eventsCount", { n: fmt.num(total) })}
        </span>
      </div>

      {/* ── Tabla ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
        borderRadius: 18, overflow: "hidden",
        backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
        boxShadow: "var(--clr-shadow-sm)",
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--clr-text4)" }}>{t("activity.loading")}</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center", color: "var(--clr-text4)", fontSize: "0.85rem" }}>
            {filterOrg || filterAction ? t("activity.emptyFilters") : t("activity.emptyYet")}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--clr-border2)" }}>
                {[t("activity.colDateTime"), t("activity.colUser"), t("activity.colOrg"), t("activity.colAction"), t("activity.colDetail"), t("activity.colIp")].map(h => (
                  <th key={h} style={{
                    padding: "0.7rem 14px", textAlign: "left",
                    color: "var(--clr-text4)", fontWeight: 600,
                    fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.9,
                    whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <>
                  <tr
                    key={item.id}
                    onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                    style={{
                      borderTop: "1px solid var(--clr-border2)",
                      cursor: "pointer",
                      background: expanded === item.id ? "var(--clr-hover)" : "transparent",
                      transition: "background 0.1s",
                    }}
                  >
                    {/* Fecha */}
                    <td style={{ padding: "9px 14px", whiteSpace: "nowrap", color: "var(--clr-text3)", fontVariantNumeric: "tabular-nums" }}>
                      {formatDate(item.created_at, fmt.lang)}
                    </td>

                    {/* Usuario */}
                    <td style={{ padding: "9px 14px", maxWidth: 200 }}>
                      <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.user_email ?? <span style={{ color: "var(--clr-text4)" }}>—</span>}
                      </div>
                      {item.user_role && (
                        <div style={{ marginTop: 3 }}>
                          <span style={{
                            display: "inline-block",
                            background: roleColor(item.user_role) + "1e", color: roleColor(item.user_role),
                            padding: "1px 8px", borderRadius: 999,
                            fontSize: "0.66rem", fontWeight: 600, letterSpacing: 0.2,
                          }}>{item.user_role}</span>
                        </div>
                      )}
                    </td>

                    {/* Org */}
                    <td style={{ padding: "9px 14px", color: "var(--clr-text3)" }}>
                      {item.organization_name ?? <span style={{ color: "var(--clr-text4)" }}>{t("activity.systemOrg")}</span>}
                    </td>

                    {/* Acción */}
                    <td style={{ padding: "9px 14px" }}>
                      <ActionBadge action={item.action} />
                    </td>

                    {/* Detalle resumido */}
                    <td style={{ padding: "9px 14px", maxWidth: 320 }}>
                      <DetailCell detail={item.detail} />
                    </td>

                    {/* IP */}
                    <td style={{ padding: "9px 14px", color: "var(--clr-text4)", fontVariantNumeric: "tabular-nums", fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                      {item.ip_address ?? "—"}
                    </td>
                  </tr>

                  {/* Fila expandida con user-agent y detalle completo */}
                  {expanded === item.id && (
                    <tr key={`${item.id}-exp`} style={{ background: "var(--clr-hover)" }}>
                      <td colSpan={6} style={{ padding: "8px 14px 14px 14px" }}>
                        <div style={{ display: "flex", gap: 32, flexWrap: "wrap", fontSize: "0.75rem", color: "var(--clr-text3)" }}>
                          {item.resource_type && (
                            <div><span style={{ color: "var(--clr-text4)", fontWeight: 600 }}>{t("activity.resourceLabel")}</span> {item.resource_type} {item.resource_id ? `(${item.resource_id})` : ""}</div>
                          )}
                          {item.user_agent && (
                            <div style={{ flex: 1 }}><span style={{ color: "var(--clr-text4)", fontWeight: 600 }}>User-Agent:</span> {item.user_agent}</div>
                          )}
                          {item.detail && Object.keys(item.detail).length > 0 && (
                            <div style={{ flex: 2 }}>
                              <span style={{ color: "var(--clr-text4)", fontWeight: 600 }}>{t("activity.fullDetailLabel")}</span>{" "}
                              <code style={{ fontSize: "0.7rem", background: "var(--clr-bg)", padding: "1px 6px", borderRadius: 4 }}>
                                {JSON.stringify(item.detail)}
                              </code>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Paginación ────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            style={{ ...inputStyle, borderRadius: 999, cursor: page === 1 ? "default" : "pointer", opacity: page === 1 ? 0.4 : 1 }}
          >{t("activity.prev")}</button>
          <span style={{ fontSize: "0.8rem", color: "var(--clr-text3)" }}>
            {t("activity.pageOf", { page, total: totalPages })}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
            style={{ ...inputStyle, borderRadius: 999, cursor: page === totalPages ? "default" : "pointer", opacity: page === totalPages ? 0.4 : 1 }}
          >{t("activity.next")}</button>
        </div>
      )}
    </div>
  );
}
